import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Heart, Award, Users } from "lucide-react";
import { EnquiryModal } from "./EnquiryModal";

export const Hero = () => {
  const heroImages = [
    "https://customer-assets.emergentagent.com/job_81c4535c-71eb-4a2b-bbef-6e28bbec7a64/artifacts/5s8rb7kd_DSC_0096-1024x683.jpg",
    "https://customer-assets.emergentagent.com/job_81c4535c-71eb-4a2b-bbef-6e28bbec7a64/artifacts/bjljnzym_20220809_161104.jpg",
    "https://customer-assets.emergentagent.com/job_81c4535c-71eb-4a2b-bbef-6e28bbec7a64/artifacts/6j9876km_20220809_161510-1024x768.jpg",
    "https://customer-assets.emergentagent.com/job_81c4535c-71eb-4a2b-bbef-6e28bbec7a64/artifacts/4o9koi67_DSC_0064-1024x683.jpg"
  ];

  const [currentImage, setCurrentImage] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [enquiryType, setEnquiryType] = useState("general");

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  const openModal = (type) => {
    setEnquiryType(type);
    setIsModalOpen(true);
  };

  return (
    <section className="relative min-h-[700px] flex items-center overflow-hidden">
      {/* Background Image Slider */}
      {heroImages.map((img, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentImage ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={img}
            alt={`Care at Barnston Court ${index + 1}`}
            className="w-full h-full object-cover object-center"
            style={{ imageRendering: 'auto', WebkitBackfaceVisibility: 'hidden' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-teal-900/90 via-teal-800/70 to-teal-700/30"></div>
        </div>
      ))}

      {/* Content */}
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl">
          {/* Badge */}
          <Badge className="mb-6 bg-orange-500 text-white hover:bg-orange-600 px-4 py-2 text-sm">
            <Heart className="h-4 w-4 mr-2" />
            Now Proudly Operated by Cozee Care
          </Badge>

          {/* Main Heading */}
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            Barnston Court
            <span className="block text-orange-400 mt-2">Nursing Home</span>
          </h1>

          {/* Subheading */}
          <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
            Compassionate residential, nursing and dementia care in Moreton, Wirral.
          </p>

          {/* Features */}
          <div className="flex flex-wrap gap-6 mb-10 text-white">
            <div className="flex items-center gap-2">
              <Award className="h-5 w-5 text-orange-400" />
              <span>CQC Rated Good</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-orange-400" />
              <span>24/7 Professional Care</span>
            </div>
            <div className="flex items-center gap-2">
              <Heart className="h-5 w-5 text-orange-400" />
              <span>Person-Centred Approach</span>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4">
            <Button
              onClick={() => openModal("visit")}
              data-testid="hero-book-visit-btn"
              size="lg"
              className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all"
            >
              Book a Visit
            </Button>
            <Button
              onClick={() => openModal("brochure")}
              data-testid="hero-request-brochure-btn"
              size="lg"
              variant="outline"
              className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-teal-700 px-8 py-6 text-lg transition-all"
            >
              Request a Brochure
            </Button>
            <Button
              onClick={() => openModal("speak")}
              data-testid="hero-speak-team-btn"
              size="lg"
              variant="outline"
              className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-teal-700 px-8 py-6 text-lg transition-all"
            >
              Speak to Our Team
            </Button>
          </div>
        </div>
      </div>

      {/* Slider Indicators */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
        {heroImages.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentImage(index)}
            className={`w-3 h-3 rounded-full transition-all ${
              index === currentImage
                ? "bg-orange-500 w-8"
                : "bg-white/50 hover:bg-white/75"
            }`}
          />
        ))}
      </div>

      {/* Enquiry Modal */}
      <EnquiryModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        enquiryType={enquiryType}
      />
    </section>
  );
};
